import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import PendingTasks from '../components/PendingTasks';
import { Users, Package, AlertTriangle, Clock, ArrowRight, RefreshCw, ListTodo, FileEdit, Trash2, Play, Mail, MailOpen, BellRing, Plus, Check, Calendar, X, Truck, Camera, ChevronDown, FileText, MapPin } from 'lucide-react';
import { toast } from 'sonner';

function KPICard({ icon: Icon, label, value, sub, color, onClick }) {
  return (
    <div
      className={`bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm hover:shadow-md transition-all cursor-pointer ${onClick ? 'hover:-translate-y-0.5' : ''}`}
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/ /g,'-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className={`text-3xl font-bold mt-1 ${color || 'text-[hsl(222,47%,11%)]'}`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl ${color ? `bg-${color.split('-')[1]}-50` : 'bg-slate-50'}`}>
          <Icon size={20} className={color || 'text-slate-600'} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentCommandes, setRecentCommandes] = useState([]);
  const [taskStats, setTaskStats] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [deletingDraft, setDeletingDraft] = useState(null);
  const [emailNotifs, setEmailNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [emailStats, setEmailStats] = useState(null);
  const [reminders, setReminders] = useState({ overdue: [], today: [], week: [], later: [] });
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [newReminder, setNewReminder] = useState({ title: '', due_date: '', client_id: '', client_name: '' });
  const [clients, setClients] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [livraisonsAValider, setLivraisonsAValider] = useState([]);
  const [updatingLivraison, setUpdatingLivraison] = useState(null);
  const [processingConfirmation, setProcessingConfirmation] = useState(null);
  const [kpiData, setKpiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchEmailNotifs = useCallback(async () => {
    try {
      const res = await api.get('/email-notifs');
      setEmailNotifs(res.data || []);
      const unread = (res.data || []).filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch {}
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clientsRes, commandesRes, tasksRes, draftsRes, emailStatsRes, remindersRes, clientsListRes, confsRes, livraisonsRes, kpiRes] = await Promise.all([
        api.get('/clients?limit=1').catch(() => ({ data: { total: 0, clients: [] } })),
        api.get('/commandes?limit=20').catch(() => ({ data: { total: 0, commandes: [] } })),
        api.get('/tasks/stats/summary').catch(() => ({ data: null })),
        api.get('/drafts').catch(() => ({ data: [] })),
        api.get('/clients/stats/email-status').catch(() => ({ data: null })),
        api.get('/reminders/grouped').catch(() => ({ data: { overdue: [], today: [], week: [], later: [] } })),
        api.get('/clients?limit=100').catch(() => ({ data: { clients: [] } })),
        api.get('/confirmations/pending/all').catch(() => ({ data: [] })),
        api.get('/commandes/livraisons-a-valider').catch(() => ({ data: { livraisons: [] } })),
        api.get('/commandes/stats/kpi').catch(() => ({ data: { kpi: [] } })),
      ]);

      const totalClients = clientsRes.data?.total || 0;
      const commandes = commandesRes.data?.commandes || [];
      const totalCommandes = commandesRes.data?.total || 0;

      const enCours = commandes.filter(c => !['facture_reglee','annule'].includes(c.statut)).length;
      const relances = commandes.filter(c => ['incomplet_j15','incomplet_j30','dem_docs_complem','litige_paiement'].includes(c.statut)).length;

      setStats({ totalClients, totalCommandes, enCours, relances });
      setRecentCommandes(commandes.slice(0, 8));
      setTaskStats(tasksRes.data);
      setDrafts(draftsRes.data || []);
      setEmailStats(emailStatsRes.data);
      setReminders(remindersRes.data || { overdue: [], today: [], week: [], later: [] });
      setClients(clientsListRes.data?.clients || []);
      setConfirmations(confsRes.data || []);
      setLivraisonsAValider(livraisonsRes.data?.livraisons || []);
      setKpiData(kpiRes.data?.kpi || []);

      // Fetch notifications
      try {
        const [notifsRes, notifCountRes] = await Promise.all([
          api.get('/notifications').catch(() => ({ data: [] })),
          api.get('/notifications/unread-count').catch(() => ({ data: { count: 0 } })),
        ]);
        setNotifications(notifsRes.data || []);
        setNotifCount(notifCountRes.data?.count || 0);
      } catch {}
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    fetchEmailNotifs();
    // Poll email notifs every 5 minutes
    const interval = setInterval(fetchEmailNotifs, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchEmailNotifs]);

  const handleMarkRead = async (notifId) => {
    try {
      await api.put(`/email-notifs/${notifId}/read`);
      setEmailNotifs(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/email-notifs/read-all/all');
      setEmailNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleDeleteNotif = async (notifId) => {
    try {
      await api.delete(`/email-notifs/${notifId}`);
      const updated = emailNotifs.filter(n => n.id !== notifId);
      setEmailNotifs(updated);
      setUnreadCount(updated.filter(n => !n.read).length);
    } catch {}
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Supprimer ce brouillon définitivement ?')) return;
    setDeletingDraft(draftId);
    try {
      await api.delete(`/drafts/${draftId}`);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingDraft(null);
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.title || !newReminder.due_date) {
      toast.error('Titre et date requis');
      return;
    }
    try {
      await api.post('/reminders', newReminder);
      toast.success('Rappel créé');
      setShowReminderModal(false);
      setNewReminder({ title: '', due_date: '', client_id: '', client_name: '' });
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleToggleReminder = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/toggle`);
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await api.delete(`/reminders/${reminderId}`);
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleValidateLivraison = async (commandeId, newStatut) => {
    setUpdatingLivraison(commandeId);
    try {
      await api.put(`/commandes/${commandeId}/statut`, { statut: newStatut, commentaire: 'Validé depuis le dashboard après livraison' });
      toast.success(`Statut mis à jour → ${newStatut}`);
      setLivraisonsAValider(prev => prev.filter(l => l.id !== commandeId));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setUpdatingLivraison(null);
    }
  };

  const handleAdminConfirmation = async (token, action) => {
    setProcessingConfirmation(token);
    try {
      const res = await api.put(`/confirmations/${token}/admin-${action}`);
      toast.success(res.data.message);
      setConfirmations(prev => prev.map(c => c.token === token ? { ...c, status: action === 'confirm' ? 'confirmee' : 'refusee' } : c));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setProcessingConfirmation(null);
    }
  };

  const formatDueDate = (isoDate) => {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const formatRelativeDate = (isoDate) => {
    if (!isoDate) return '';
    try {
      const diff = Date.now() - new Date(isoDate).getTime();
      const mins = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      if (mins < 2) return "à l'instant";
      if (hours < 1) return `il y a ${mins} min`;
      if (hours < 24) return `il y a ${hours}h`;
      if (days === 1) return 'hier';
      return `il y a ${days} jours`;
    } catch { return ''; }
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center">
      <RefreshCw size={20} className="animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)]">Tableau de bord</h1>
          <p className="text-gray-500 text-sm">Vue d'ensemble de vos dossiers</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="refresh-btn">
          <RefreshCw size={14} />
          Actualiser
        </button>
      </div>

      {/* KPI Cards — Chiffre d'Affaires par phase */}
      {kpiData.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-cards-section">
          {kpiData.sort((a, b) => (a.order || 0) - (b.order || 0)).map(kpi => (
            <div key={kpi.id} className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`kpi-card-${kpi.id}`}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{kpi.nom}</p>
              {kpi.show_count && (
                <p className="text-2xl font-bold text-[hsl(222,47%,11%)]">
                  {kpi.count}
                  <span className="text-sm font-normal text-gray-400 ml-1">commande{kpi.count > 1 ? 's' : ''}</span>
                </p>
              )}
              {kpi.show_ca && (
                <p className="text-lg font-semibold text-emerald-600 mt-1">
                  {(kpi.ca_ht || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                  <span className="text-xs font-normal text-gray-400 ml-1">HT</span>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fiches en cours de création (Brouillons) */}
      {drafts.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm" data-testid="drafts-section">
          <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100 bg-amber-50 rounded-t-xl">
            <h2 className="font-semibold text-amber-900 flex items-center gap-2">
              <FileEdit size={18} className="text-amber-600" />
              Fiches en cours de création
              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">
                {drafts.length}
              </span>
            </h2>
          </div>
          <div className="divide-y divide-gray-50">
            {drafts.map(draft => (
              <div key={draft.id} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
                <div>
                  <p className="font-medium text-gray-900 text-sm" data-testid={`draft-name-${draft.id}`}>
                    {draft.societe || 'Sans nom'}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Étape {draft.current_step}/10 · {formatRelativeDate(draft.updated_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/clients/nouveau?draft=${draft.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[hsl(222,47%,11%)] text-white text-xs font-medium rounded-lg hover:bg-[hsl(222,47%,18%)] transition-colors"
                    data-testid={`draft-resume-${draft.id}`}
                  >
                    <Play size={11} />
                    Reprendre
                  </button>
                  <button
                    onClick={() => handleDeleteDraft(draft.id)}
                    disabled={deletingDraft === draft.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-500 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    data-testid={`draft-delete-${draft.id}`}
                  >
                    <Trash2 size={11} />
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Livraisons effectuées — À valider par l'admin */}
      {livraisonsAValider.length > 0 && (
        <div className="bg-white rounded-xl border border-emerald-200 shadow-sm" data-testid="livraisons-a-valider-section">
          <div className="flex items-center justify-between px-5 py-4 border-b border-emerald-100 bg-emerald-50 rounded-t-xl">
            <h2 className="font-semibold text-emerald-900 flex items-center gap-2">
              <Truck size={18} className="text-emerald-600" />
              Livraisons effectuées — À valider
              <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
                {livraisonsAValider.length}
              </span>
            </h2>
          </div>
          <div className="divide-y divide-gray-50">
            {livraisonsAValider.map(liv => (
              <div key={liv.id} className="px-5 py-4 hover:bg-gray-50 transition-colors" data-testid={`livraison-item-${liv.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{liv.client_societe}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500 font-mono">{liv.numero}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {liv.modele}{liv.lignes?.length ? '' : ` x${liv.quantite}`}
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="text-gray-500">Livré par <strong>{liv.livreur_nom || '—'}</strong></span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="text-gray-400">{formatRelativeDate(liv.date_livraison)}</span>
                    </p>
                    {liv.compte_rendu_livraison && (
                      <div className="mt-2 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-100">
                        <p className="text-xs font-medium text-emerald-700 flex items-center gap-1 mb-0.5">
                          <FileText size={11} /> Compte-rendu
                        </p>
                        <p className="text-xs text-emerald-800">{liv.compte_rendu_livraison}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {liv.client_ville && (
                        <span className="flex items-center gap-1"><MapPin size={11} />{liv.client_cp} {liv.client_ville}</span>
                      )}
                      <span className="flex items-center gap-1"><Camera size={11} />{liv.photo_count} photo{liv.photo_count > 1 ? 's' : ''}</span>
                      <span>Statut actuel : <StatusBadge statut={liv.statut} /></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/commandes/${liv.id}`)}
                      className="px-3 py-1.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid={`livraison-view-${liv.id}`}
                    >
                      Voir la fiche
                    </button>
                    <button
                      onClick={() => handleValidateLivraison(liv.id, 'livre')}
                      disabled={updatingLivraison === liv.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs font-bold rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
                      data-testid={`livraison-validate-${liv.id}`}
                    >
                      {updatingLivraison === liv.id ? (
                        <RefreshCw size={11} className="animate-spin" />
                      ) : (
                        <Check size={11} />
                      )}
                      Passer en "Livré"
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notifications emails */}
      {emailNotifs.length > 0 && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm" data-testid="email-notifs-section">
          <div className="flex items-center justify-between px-5 py-4 border-b border-blue-100 bg-blue-50 rounded-t-xl">
            <h2 className="font-semibold text-blue-900 flex items-center gap-2">
              <Mail size={18} className="text-blue-600" />
              Emails reçus
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-full animate-pulse">
                  {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </h2>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                data-testid="mark-all-read-btn"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-50">
            {emailNotifs.slice(0, 8).map(notif => (
              <div
                key={notif.id}
                className={`flex items-start justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors ${!notif.read ? 'bg-blue-50/40' : ''}`}
                data-testid={`email-notif-${notif.id}`}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!notif.read ? 'bg-blue-500' : 'bg-gray-200'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{notif.client_societe}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500 truncate max-w-[200px]">{notif.from_addr}</span>
                    </div>
                    <p className="text-sm text-gray-700 truncate mt-0.5">{notif.subject}</p>
                    {notif.preview && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{notif.preview}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{formatRelativeDate(notif.received_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                  <button
                    onClick={() => navigate(`/clients/${notif.client_id}`)}
                    className="px-2.5 py-1 bg-[hsl(222,47%,11%)] text-white text-xs rounded-lg hover:bg-[hsl(222,47%,18%)] transition-colors"
                    data-testid={`email-notif-view-${notif.id}`}
                  >
                    Voir
                  </button>
                  {!notif.read && (
                    <button
                      onClick={() => handleMarkRead(notif.id)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Marquer comme lu"
                      data-testid={`email-notif-read-${notif.id}`}
                    >
                      <MailOpen size={13} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteNotif(notif.id)}
                    className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                    data-testid={`email-notif-delete-${notif.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Boîtes Email Status */}
      {emailStats && (
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm" data-testid="email-status-card">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail size={18} className="text-[hsl(222,47%,11%)]" />
            État des boîtes email
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <button
              onClick={() => navigate('/clients?email_status=active')}
              className="text-center p-3 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors cursor-pointer"
              data-testid="email-active-btn"
            >
              <p className="text-2xl font-bold text-emerald-600">{emailStats.actives}</p>
              <p className="text-xs text-emerald-700 mt-1">Actives</p>
            </button>
            {(emailStats.erreur_auth > 0) && (
              <button
                onClick={() => navigate('/clients?email_status=erreur_auth')}
                className="text-center p-3 bg-red-50 rounded-xl hover:bg-red-100 transition-colors cursor-pointer"
                data-testid="email-erreur-auth-btn"
              >
                <p className="text-2xl font-bold text-red-600">{emailStats.erreur_auth}</p>
                <p className="text-xs text-red-700 mt-1">Erreur auth</p>
              </button>
            )}
            {(emailStats.erreur_connexion > 0) && (
              <button
                onClick={() => navigate('/clients?email_status=erreur_connexion')}
                className="text-center p-3 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors cursor-pointer"
                data-testid="email-erreur-conn-btn"
              >
                <p className="text-2xl font-bold text-orange-600">{emailStats.erreur_connexion}</p>
                <p className="text-xs text-orange-700 mt-1">Erreur connexion</p>
              </button>
            )}
            {(emailStats.inactives > 0) && (
              <button
                onClick={() => navigate('/clients?email_status=inactive')}
                className="text-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                data-testid="email-inactive-btn"
              >
                <p className="text-2xl font-bold text-gray-600">{emailStats.inactives}</p>
                <p className="text-xs text-gray-700 mt-1">Inactives</p>
              </button>
            )}
            {(emailStats.pending > 0) && (
              <button
                onClick={() => navigate('/clients?email_status=pending')}
                className="text-center p-3 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors cursor-pointer"
                data-testid="email-pending-btn"
              >
                <p className="text-2xl font-bold text-amber-600">{emailStats.pending}</p>
                <p className="text-xs text-amber-700 mt-1">En attente</p>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Suivi Techniciens */}
      {(confirmations.length > 0 || notifications.length > 0) && (() => {
        const enAttente = confirmations.filter(c => c.status === 'en_attente');
        const aReassigner = confirmations.filter(c => c.status === 'refusee');
        const activiteRecente = notifications.slice(0, 8);

        return (
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm" data-testid="suivi-techniciens">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users size={18} className="text-[hsl(222,47%,11%)]" />
            Suivi Techniciens
          </h3>

          {/* À réassigner */}
          {aReassigner.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-red-500 uppercase mb-2 flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full" />
                À réassigner ({aReassigner.length})
              </p>
              <div className="space-y-2">
                {aReassigner.map(conf => (
                  <div key={conf.token} className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 cursor-pointer transition-colors"
                    onClick={() => navigate(conf.type_tache === 'intervention' ? `/interventions/${conf.tache_id}` : `/commandes/${conf.tache_id}`)}>
                    <X size={16} className="text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {conf.type_tache === 'intervention' ? '🔧' : '🚚'} {conf.client_societe} — {conf.detail}
                      </p>
                      <p className="text-xs text-gray-500">{conf.technicien_nom} a refusé</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* En attente */}
          {enAttente.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-amber-600 uppercase mb-2 flex items-center gap-1">
                <span className="w-2 h-2 bg-amber-500 rounded-full" />
                En attente de confirmation ({enAttente.length})
              </p>
              <div className="space-y-2">
                {enAttente.map(conf => {
                  const daysSince = Math.floor((Date.now() - new Date(conf.created_at).getTime()) / 86400000);
                  const isProcessing = processingConfirmation === conf.token;
                  return (
                    <div key={conf.token} className="p-3 rounded-lg bg-amber-50 border border-amber-200 transition-colors">
                      <div className="flex items-center gap-3">
                        <Clock size={16} className="text-amber-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(conf.type_tache === 'intervention' ? `/interventions/${conf.tache_id}` : `/commandes/${conf.tache_id}`)}>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {conf.type_tache === 'intervention' ? '🔧' : '🚚'} {conf.client_societe} — {conf.detail}
                          </p>
                          <p className="text-xs text-gray-500">{conf.technicien_nom} · en attente depuis {daysSince}j</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 ml-7">
                        <button
                          onClick={() => handleAdminConfirmation(conf.token, 'confirm')}
                          disabled={isProcessing}
                          className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
                          data-testid={`admin-confirm-${conf.token}`}
                        >
                          {isProcessing ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />}
                          Confirmer pour le livreur
                        </button>
                        <button
                          onClick={() => handleAdminConfirmation(conf.token, 'refuse')}
                          disabled={isProcessing}
                          className="flex items-center gap-1 px-2.5 py-1 border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                          data-testid={`admin-refuse-${conf.token}`}
                        >
                          <X size={11} />
                          Refuser pour le livreur
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Activité récente */}
          {activiteRecente.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                Activité récente
              </p>
              <div className="space-y-2">
                {activiteRecente.map(n => (
                  <div key={n.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => {
                      if (n.type_tache === 'intervention') navigate(`/interventions/${n.tache_id}`);
                      else if (n.tache_id) navigate(`/commandes/${n.tache_id}`);
                    }}>
                    {(n.statut === 'en_cours' || n.statut === 'livree') && <Check size={16} className="text-emerald-500 flex-shrink-0" />}
                    {(n.statut === 'terminee') && <Check size={16} className="text-blue-500 flex-shrink-0" />}
                    {n.statut === 'a_reassigner' && <X size={16} className="text-red-500 flex-shrink-0" />}
                    {!['en_cours','livree','terminee','a_reassigner'].includes(n.statut) && <Check size={16} className="text-gray-400 flex-shrink-0" />}
                    <p className="text-sm text-gray-700 flex-1">{n.message}</p>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{formatRelativeDate(n.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {enAttente.length === 0 && aReassigner.length === 0 && activiteRecente.length === 0 && (
            <div className="text-center py-6 text-gray-400 text-sm">Aucune activité technicien</div>
          )}
        </div>
        );
      })()}

      {/* Widget Rappels */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] shadow-sm" data-testid="reminders-widget">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calendar size={18} className="text-[hsl(222,47%,11%)]" />
            Mes rappels
            {(reminders.overdue.length + reminders.today.length) > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                {reminders.overdue.length + reminders.today.length}
              </span>
            )}
          </h2>
          <button
            onClick={() => setShowReminderModal(true)}
            className="flex items-center gap-1 text-sm text-[hsl(222,47%,11%)] hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors"
            data-testid="new-reminder-btn"
          >
            <Plus size={14} />
            Nouveau
          </button>
        </div>
        <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
          {/* En retard */}
          {reminders.overdue.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-red-600 mb-2 flex items-center gap-1">
                <AlertTriangle size={12} />
                En retard ({reminders.overdue.length})
              </p>
              <div className="space-y-2">
                {reminders.overdue.map(r => (
                  <div key={r.id} className="flex items-center gap-3 p-2.5 bg-red-50 rounded-lg border border-red-200">
                    <button onClick={() => handleToggleReminder(r.id)} className="w-5 h-5 rounded border-2 border-red-400 flex items-center justify-center hover:bg-red-100">
                      <Check size={12} className="text-red-400 opacity-0 hover:opacity-100" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                      {r.client_name && <p className="text-xs text-gray-500">{r.client_name}</p>}
                    </div>
                    <span className="text-xs text-red-600 font-medium">{formatDueDate(r.due_date)}</span>
                    <button onClick={() => handleDeleteReminder(r.id)} className="p-1 hover:bg-red-100 rounded">
                      <X size={14} className="text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aujourd'hui */}
          {reminders.today.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-600 mb-2">Aujourd'hui ({reminders.today.length})</p>
              <div className="space-y-2">
                {reminders.today.map(r => (
                  <div key={r.id} className="flex items-center gap-3 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                    <button onClick={() => handleToggleReminder(r.id)} className="w-5 h-5 rounded border-2 border-amber-400 flex items-center justify-center hover:bg-amber-100">
                      <Check size={12} className="text-amber-400 opacity-0 hover:opacity-100" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                      {r.client_name && <p className="text-xs text-gray-500">{r.client_name}</p>}
                    </div>
                    <button onClick={() => handleDeleteReminder(r.id)} className="p-1 hover:bg-amber-100 rounded">
                      <X size={14} className="text-amber-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cette semaine */}
          {reminders.week.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">Cette semaine ({reminders.week.length})</p>
              <div className="space-y-2">
                {reminders.week.map(r => (
                  <div key={r.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                    <button onClick={() => handleToggleReminder(r.id)} className="w-5 h-5 rounded border-2 border-gray-300 flex items-center justify-center hover:bg-gray-100">
                      <Check size={12} className="text-gray-300 opacity-0 hover:opacity-100" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                      {r.client_name && <p className="text-xs text-gray-500">{r.client_name}</p>}
                    </div>
                    <span className="text-xs text-gray-500">{formatDueDate(r.due_date)}</span>
                    <button onClick={() => handleDeleteReminder(r.id)} className="p-1 hover:bg-gray-100 rounded">
                      <X size={14} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plus tard */}
          {reminders.later.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 mb-2">Plus tard ({reminders.later.length})</p>
              <div className="space-y-2">
                {reminders.later.slice(0, 3).map(r => (
                  <div key={r.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                    <button onClick={() => handleToggleReminder(r.id)} className="w-5 h-5 rounded border-2 border-gray-200 flex items-center justify-center hover:bg-gray-100">
                      <Check size={12} className="text-gray-200 opacity-0 hover:opacity-100" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 truncate">{r.title}</p>
                      {r.client_name && <p className="text-xs text-gray-400">{r.client_name}</p>}
                    </div>
                    <span className="text-xs text-gray-400">{formatDueDate(r.due_date)}</span>
                    <button onClick={() => handleDeleteReminder(r.id)} className="p-1 hover:bg-gray-100 rounded">
                      <X size={14} className="text-gray-300" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vide */}
          {reminders.overdue.length === 0 && reminders.today.length === 0 && reminders.week.length === 0 && reminders.later.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <Calendar size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun rappel</p>
              <button
                onClick={() => setShowReminderModal(true)}
                className="mt-2 text-sm text-[hsl(222,47%,11%)] hover:underline"
              >
                Créer un rappel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal Nouveau Rappel */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Calendar size={18} />
                Nouveau rappel
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Titre *</label>
                <input
                  type="text"
                  value={newReminder.title}
                  onChange={e => setNewReminder(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  placeholder="Ex: Relancer pour documents"
                  data-testid="reminder-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Date d'échéance *</label>
                <input
                  type="date"
                  value={newReminder.due_date}
                  onChange={e => setNewReminder(p => ({ ...p, due_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  data-testid="reminder-date-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Client (optionnel)</label>
                <select
                  value={newReminder.client_id}
                  onChange={e => {
                    const client = clients.find(c => c.id === e.target.value);
                    setNewReminder(p => ({ ...p, client_id: e.target.value, client_name: client?.societe || '' }));
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  data-testid="reminder-client-select"
                >
                  <option value="">-- Aucun client --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.societe}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => { setShowReminderModal(false); setNewReminder({ title: '', due_date: '', client_id: '', client_name: '' }); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateReminder}
                className="px-4 py-2 text-sm bg-[hsl(222,47%,11%)] text-white rounded-lg hover:bg-[hsl(222,47%,18%)] transition-colors"
                data-testid="create-reminder-btn"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tâches en attente */}
      {(taskStats?.total_pending > 0 || taskStats?.ready > 0) && (
        <div>
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <ListTodo size={18} className="text-amber-500" />
            Tâches à finaliser
            {taskStats?.ready > 0 && (
              <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full animate-pulse">
                {taskStats.ready} PRÊTE(S)
              </span>
            )}
          </h2>
          <PendingTasks showCompleted={false} />
        </div>
      )}
    </div>
  );
}
